import { useEffect, useState } from "react";
import api, { apiError } from "../../lib/api";
import { loadPixel, trackEvent } from "../../lib/tracking";
import { Save, Eye, EyeOff, Zap, Truck, BarChart3, Mail, Store } from "lucide-react";
import { toast } from "sonner";

const EMAIL_TEMPLATE_META = [
  { id: "order_confirmation", label: "Order confirmation", desc: "Sent to the customer right after a successful order.", vars: "{{order_number}} {{first_name}} {{customer_name}} {{subtotal}} {{shipping}} {{tax}} {{total}}" },
  { id: "admin_alert", label: "New order alert (to you)", desc: "Sent to your admin alert email for every new order.", vars: "{{order_number}} {{customer_name}} {{customer_email}} {{customer_phone}} {{total}}" },
  { id: "status_shipped", label: "Order shipped", desc: "Sent when an order's fulfillment status is set to Shipped.", vars: "{{order_number}} {{first_name}} {{customer_name}} {{tracking_id}}" },
  { id: "status_delivered", label: "Order delivered", desc: "Sent when an order's fulfillment status is set to Delivered.", vars: "{{order_number}} {{first_name}} {{customer_name}}" },
  { id: "status_cancelled", label: "Order cancelled", desc: "Sent when an order's fulfillment status is set to Cancelled.", vars: "{{order_number}} {{first_name}} {{customer_name}}" },
  { id: "status_generic", label: "Other status update", desc: "Fallback for any fulfillment status besides shipped/delivered/cancelled.", vars: "{{order_number}} {{first_name}} {{customer_name}} {{status}}" },
  { id: "abandoned_cart", label: "Abandoned cart recovery", desc: "Sent ~1 hour after a shopper leaves checkout without completing the purchase.", vars: "{{first_name}}" },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("razorpay");
  const [emailTplType, setEmailTplType] = useState("order_confirmation");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSettings(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const update = (section, key, value) =>
    setSettings((s) => ({ ...s, [section]: { ...s[section], [key]: value } }));

  const updateTemplate = (type, field, value) =>
    setSettings((s) => ({
      ...s,
      email_templates: { ...s.email_templates, [type]: { ...s.email_templates[type], [field]: value } },
    }));

  const save = async (sectionKey) => {
    setSaving(true);
    try {
      // Replace masked secrets with empty strings so backend preserves existing
      const sec = { ...settings[sectionKey] };
      Object.keys(sec).forEach((k) => {
        if (typeof sec[k] === "string" && sec[k].startsWith("••••")) sec[k] = "";
      });
      const { data } = await api.put("/admin/settings", { [sectionKey]: sec });
      setSettings(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const test = async (endpoint, body) => {
    try {
      const { data } = await api.post(`/admin/settings/test/${endpoint}`, body || {});
      toast.success(data.message || "Connected");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Fires a real Purchase event straight from the browser Pixel — needs
  // only a saved Pixel ID, no CAPI access token, since it never touches
  // the backend/Graph API. A best-effort server-side CAPI mirror is
  // still attempted by trackEvent() but silently no-ops if CAPI isn't
  // configured, so this is safe to use even with no token set at all.
  const sendBrowserTestPurchase = () => {
    const pixelId = settings.meta.pixel_id;
    if (!pixelId) {
      toast.error("Save a Pixel ID above first.");
      return;
    }
    loadPixel(pixelId);
    trackEvent("Purchase", {
      value: 999,
      currency: "INR",
      content_ids: ["test-product"],
      content_name: "Test Product",
    });
    toast.success("Test Purchase event sent from this browser. Check Meta Events Manager — Test Events tab if you've set a test event code, otherwise Overview may take a few minutes to update.");
  };

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const webhookURL = `${backendBase}/api/webhooks/razorpay`;

  if (loading || !settings) {
    return <div className="p-10 font-script text-3xl text-blush-600">loading…</div>;
  }

  const TABS = [
    { id: "razorpay", label: "Razorpay", icon: Zap },
    { id: "shiprocket", label: "Shiprocket", icon: Truck },
    { id: "meta", label: "Meta · Pixel · CAPI", icon: BarChart3 },
    { id: "smtp", label: "SMTP · Transactional", icon: Mail },
    { id: "smtp_marketing", label: "SMTP · Marketing", icon: Mail },
    { id: "email_templates", label: "Email templates", icon: Mail },
    { id: "size_chart", label: "Size chart", icon: Store },
    { id: "store", label: "Store", icon: Store },
  ];

  return (
    <div className="p-6 md:p-10 max-w-5xl" data-testid="admin-settings">
      <header className="mb-10">
        <span className="font-script text-3xl text-blush-400">the wiring</span>
        <h1 className="font-heading text-3xl md:text-4xl text-ink-900">Settings & integrations</h1>
        <p className="mt-2 font-body text-ink-500">
          All credentials are encrypted at rest. Existing secrets show as ••••; leave blank to keep them.
        </p>
      </header>

      <div className="flex gap-2 mb-8 border-b border-blush-100 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`settings-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-5 py-3 font-body text-sm uppercase tracking-[0.18em] border-b-2 transition ${
              tab === t.id ? "border-blush-600 text-blush-600" : "border-transparent text-ink-700 hover:text-blush-600"
            }`}
          >
            <t.icon size={14} strokeWidth={1.6} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "razorpay" && (
        <Section
          title="Razorpay"
          subtitle="Toggle live/test mode anytime. Storefront uses the active mode for checkout."
          onSave={() => save("razorpay")}
          onTest={() => test("razorpay")}
          saving={saving}
        >
          <ModeToggle
            value={settings.razorpay.mode}
            onChange={(v) => update("razorpay", "mode", v)}
          />
          <div className="grid grid-cols-2 gap-4 mt-6">
            <Field label="Test Key ID" value={settings.razorpay.test_key_id} onChange={(v) => update("razorpay", "test_key_id", v)} placeholder="rzp_test_..." />
            <SecretField label="Test Key Secret" value={settings.razorpay.test_key_secret} onChange={(v) => update("razorpay", "test_key_secret", v)} />
            <Field label="Live Key ID" value={settings.razorpay.live_key_id} onChange={(v) => update("razorpay", "live_key_id", v)} placeholder="rzp_live_..." />
            <SecretField label="Live Key Secret" value={settings.razorpay.live_key_secret} onChange={(v) => update("razorpay", "live_key_secret", v)} />
            <SecretField label="Webhook secret" value={settings.razorpay.webhook_secret} onChange={(v) => update("razorpay", "webhook_secret", v)} colSpan={2} />
          </div>
          <div className="mt-6 rounded-2xl bg-blush-50/60 border border-blush-100 p-4">
            <p className="text-[11px] font-body uppercase tracking-[0.2em] text-ink-500">Webhook URL — paste into Razorpay Dashboard → Webhooks</p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 text-xs font-mono text-blush-600 bg-white border border-blush-100 rounded-lg px-3 py-2 break-all">{webhookURL}</code>
              <button type="button" onClick={() => { navigator.clipboard.writeText(webhookURL); toast.success("Copied"); }} className="btn-ghost-rose text-xs">Copy</button>
            </div>
            <p className="mt-2 text-[11px] font-body text-ink-500">Subscribe to events: <code>payment.captured</code>, <code>payment.failed</code>, <code>order.paid</code>.</p>
          </div>
        </Section>
      )}

      {tab === "shiprocket" && (
        <Section
          title="Shiprocket"
          subtitle="Used to auto-create shipments from paid orders. Token is fetched & cached automatically."
          onSave={() => save("shiprocket")}
          onTest={() => test("shiprocket")}
          saving={saving}
        >
          <div className="grid grid-cols-2 gap-4">
            <Field label="Shiprocket email" value={settings.shiprocket.email} onChange={(v) => update("shiprocket", "email", v)} />
            <SecretField label="Password" value={settings.shiprocket.password} onChange={(v) => update("shiprocket", "password", v)} />
            <Field label="Pickup location" value={settings.shiprocket.pickup_location} onChange={(v) => update("shiprocket", "pickup_location", v)} placeholder="Primary" />
          </div>
        </Section>
      )}

      {tab === "meta" && (
        <Section
          title="Meta — Ads, Pixel & CAPI"
          subtitle="Pixel ID is loaded on the storefront. Access tokens fetch ad insights and send server-side CAPI events."
          onSave={() => save("meta")}
          onTest={() => test("meta")}
          saving={saving}
        >
          <div className="grid grid-cols-2 gap-4">
            <Field label="Pixel ID" value={settings.meta.pixel_id} onChange={(v) => update("meta", "pixel_id", v)} placeholder="1234567890" />
            <Field label="Ad account ID" value={settings.meta.ad_account_id} onChange={(v) => update("meta", "ad_account_id", v)} placeholder="act_1234567890" />
            <SecretField label="Marketing API access token" value={settings.meta.access_token} onChange={(v) => update("meta", "access_token", v)} colSpan={2} hint="Used to fetch ad insights for the Reports page" />
            <SecretField label="CAPI access token (optional)" value={settings.meta.capi_access_token} onChange={(v) => update("meta", "capi_access_token", v)} colSpan={2} hint="Defaults to the Marketing API token if blank" />
            <Field label="Test event code (optional)" value={settings.meta.capi_test_event_code} onChange={(v) => update("meta", "capi_test_event_code", v)} placeholder="TEST12345" />
          </div>
          <div className="mt-6 pt-6 border-t border-blush-100">
            <p className="text-sm font-body text-ink-900">Purchase event not selectable when creating a campaign?</p>
            <p className="mt-1 text-[11px] font-body text-ink-500">
              A brand-new pixel with zero Purchase events ever recorded shows as inactive in Ads Manager. Send one test
              purchase below so Meta marks it as receiving data.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={sendBrowserTestPurchase} className="btn-ghost-rose text-xs" data-testid="settings-test-meta-purchase-browser-btn">
                Send test Purchase event (browser)
              </button>
              <button type="button" onClick={() => test("meta-purchase")} className="btn-ghost-rose text-xs" data-testid="settings-test-meta-purchase-btn">
                Send test Purchase event (server/CAPI)
              </button>
            </div>
            <p className="mt-2 text-[11px] font-body text-ink-500">
              <strong>Browser</strong> only needs a saved Pixel ID above — fires right from this page, no access token
              required. <strong>Server/CAPI</strong> needs a saved access token too, but is more reliable (works even
              with ad blockers or iOS tracking restrictions).
            </p>
          </div>
        </Section>
      )}

      {tab === "smtp" && (
        <Section
          title="Email SMTP — Transactional"
          subtitle="Used for order confirmations & admin notifications."
          onSave={() => save("smtp")}
          onTest={() => test("smtp")}
          saving={saving}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Host" value={settings.smtp.host} onChange={(v) => update("smtp", "host", v)} placeholder="smtp.gmail.com" />
            <Field label="Port" type="number" value={settings.smtp.port} onChange={(v) => update("smtp", "port", v)} placeholder="587" />
            <Field label="Username" value={settings.smtp.username} onChange={(v) => update("smtp", "username", v)} />
            <SecretField label="Password / App Password" value={settings.smtp.password} onChange={(v) => update("smtp", "password", v)} />
            <Field label="From email" value={settings.smtp.from_email} onChange={(v) => update("smtp", "from_email", v)} placeholder="hello@flownte.in" />
            <Field label="From name" value={settings.smtp.from_name} onChange={(v) => update("smtp", "from_name", v)} />
          </div>
        </Section>
      )}

      {tab === "smtp_marketing" && (
        <Section
          title="Email SMTP — Marketing"
          subtitle="Separate credentials used for abandoned-cart recovery and marketing emails. If empty, transactional SMTP is used as fallback."
          onSave={() => save("smtp_marketing")}
          onTest={() => test("smtp", { marketing: true })}
          saving={saving}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Host" value={settings.smtp_marketing?.host} onChange={(v) => update("smtp_marketing", "host", v)} />
            <Field label="Port" type="number" value={settings.smtp_marketing?.port} onChange={(v) => update("smtp_marketing", "port", v)} placeholder="587" />
            <Field label="Username" value={settings.smtp_marketing?.username} onChange={(v) => update("smtp_marketing", "username", v)} />
            <SecretField label="Password" value={settings.smtp_marketing?.password} onChange={(v) => update("smtp_marketing", "password", v)} />
            <Field label="From email" value={settings.smtp_marketing?.from_email} onChange={(v) => update("smtp_marketing", "from_email", v)} />
            <Field label="From name" value={settings.smtp_marketing?.from_name} onChange={(v) => update("smtp_marketing", "from_name", v)} />
          </div>
        </Section>
      )}

      {tab === "email_templates" && (
        <Section
          title="Email templates"
          subtitle="Edit the subject and copy of every transactional email. Use the {{variables}} shown below each field — they're replaced with the real order details when the email is sent."
          onSave={() => save("email_templates")}
          onTest={() => test("smtp")}
          saving={saving}
        >
          <div className="flex gap-2 mb-6 flex-wrap">
            {EMAIL_TEMPLATE_META.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setEmailTplType(m.id)}
                data-testid={`email-template-${m.id}`}
                className={`px-4 py-2 rounded-full text-xs font-body uppercase tracking-[0.14em] border transition ${
                  emailTplType === m.id
                    ? "bg-blush-600 text-white border-blush-600"
                    : "text-ink-700 border-blush-100 hover:border-blush-300"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {(() => {
            const meta = EMAIL_TEMPLATE_META.find((m) => m.id === emailTplType);
            const tpl = settings.email_templates?.[emailTplType] || {};
            return (
              <div>
                <p className="text-sm font-body text-ink-500 mb-4">{meta.desc}</p>
                <label className="flex items-center gap-2 text-sm font-body text-ink-900 mb-6">
                  <input
                    type="checkbox"
                    checked={tpl.enabled !== false}
                    onChange={(e) => updateTemplate(emailTplType, "enabled", e.target.checked)}
                  />
                  Send this email
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Field label="Subject" value={tpl.subject} onChange={(v) => updateTemplate(emailTplType, "subject", v)} />
                  </div>
                  <Field label="Accent tagline (small script text, optional)" value={tpl.accent} onChange={(v) => updateTemplate(emailTplType, "accent", v)} />
                  <Field label="Heading" value={tpl.title} onChange={(v) => updateTemplate(emailTplType, "title", v)} />
                  <div className="md:col-span-2">
                    <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Body text</label>
                    <textarea
                      rows={3}
                      value={tpl.intro ?? ""}
                      onChange={(e) => updateTemplate(emailTplType, "intro", e.target.value)}
                      className="field mt-2"
                    />
                  </div>
                  {["status_shipped", "status_delivered", "abandoned_cart"].includes(emailTplType) && (
                    <Field label="Button text (if this email has a button)" value={tpl.cta_label} onChange={(v) => updateTemplate(emailTplType, "cta_label", v)} />
                  )}
                  <div className="md:col-span-2">
                    <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Closing note (optional, small text)</label>
                    <textarea
                      rows={2}
                      value={tpl.note ?? ""}
                      onChange={(e) => updateTemplate(emailTplType, "note", e.target.value)}
                      className="field mt-2"
                    />
                  </div>
                </div>
                <p className="mt-3 text-[11px] font-body text-ink-500">
                  Available variables: <code className="text-blush-600">{meta.vars}</code>
                </p>
              </div>
            );
          })()}
        </Section>
      )}

      {tab === "store" && (
        <Section title="Store" subtitle="General store-wide settings." onSave={() => save("store")} saving={saving}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Store name" value={settings.store.name} onChange={(v) => update("store", "name", v)} />
            <Field label="Currency" value={settings.store.currency} onChange={(v) => update("store", "currency", v)} />
            <Field label="Free shipping above (₹)" type="number" value={settings.store.free_shipping_above} onChange={(v) => update("store", "free_shipping_above", Number(v))} />
            <Field label="Shipping fee (₹)" type="number" value={settings.store.shipping_fee} onChange={(v) => update("store", "shipping_fee", Number(v))} placeholder="99" />
            <Field label="Tax %" type="number" step="0.01" value={settings.store.tax_percent} onChange={(v) => update("store", "tax_percent", Number(v))} />
            <div className="md:col-span-2">
              <Field
                label="Admin alert email"
                value={settings.store.admin_alert_email || ""}
                onChange={(v) => update("store", "admin_alert_email", v)}
                placeholder="orders@flownte.in"
              />
              <p className="text-[11px] font-body text-ink-500 mt-1">
                You'll get an email here every time a new order is placed. Leave empty to skip admin alerts.
              </p>
            </div>
            <div className="md:col-span-2 pt-4 border-t border-blush-100">
              <label className="flex items-center gap-2 text-sm font-body text-ink-900">
                <input
                  type="checkbox"
                  checked={!!settings.store.cod_partial_pay_enabled}
                  onChange={(e) => update("store", "cod_partial_pay_enabled", e.target.checked)}
                />
                Offer "partial pay" for Cash on Delivery orders
              </label>
              <p className="text-[11px] font-body text-ink-500 mt-1">
                Customer pays a small amount online via Razorpay at checkout, and the rest in cash
                on delivery — e.g. a ₹700 order with ₹50 set below charges ₹50 online now and
                ₹650 on delivery.
              </p>
            </div>
            <Field
              label="Partial pay amount (₹)"
              type="number"
              value={settings.store.cod_partial_pay_amount}
              onChange={(v) => update("store", "cod_partial_pay_amount", Number(v))}
              placeholder="50"
            />
          </div>
        </Section>
      )}

      {tab === "size_chart" && (
        <Section
          title="Size chart"
          subtitle="This appears on product pages when shoppers click 'Size guide'."
          onSave={() => save("size_chart")}
          saving={saving}
        >
          <div className="mb-4 flex items-center gap-4">
            <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Unit</label>
            <div className="inline-flex rounded-full bg-blush-50 p-1 border border-blush-100">
              {["in", "cm"].map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => update("size_chart", "unit", u)}
                  className={`px-5 py-1.5 rounded-full text-xs font-body uppercase tracking-[0.2em] transition ${
                    settings.size_chart.unit === u ? "bg-white text-blush-600 shadow" : "text-ink-700"
                  }`}
                >
                  {u === "in" ? "Inches" : "Centimetres"}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto rounded-xl border border-blush-100">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="bg-blush-50/50 text-[11px] uppercase tracking-[0.18em] text-ink-500">
                  <th className="px-4 py-3 text-left">Size</th>
                  <th className="px-4 py-3">Bust</th>
                  <th className="px-4 py-3">Waist</th>
                  <th className="px-4 py-3">Hip</th>
                  <th className="px-4 py-3">Length</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {(settings.size_chart.rows || []).map((row, i) => (
                  <tr key={i} className="border-t border-blush-100">
                    <td className="px-4 py-2">
                      <input
                        className="field !py-1.5 !px-3 text-sm"
                        value={row.size}
                        onChange={(e) => {
                          const rows = [...settings.size_chart.rows];
                          rows[i] = { ...rows[i], size: e.target.value };
                          update("size_chart", "rows", rows);
                        }}
                      />
                    </td>
                    {["bust", "waist", "hip", "length"].map((k) => (
                      <td key={k} className="px-4 py-2">
                        <input
                          type="number"
                          step="0.5"
                          className="field !py-1.5 !px-3 text-sm text-center"
                          value={row[k] ?? ""}
                          onChange={(e) => {
                            const rows = [...settings.size_chart.rows];
                            rows[i] = { ...rows[i], [k]: Number(e.target.value) };
                            update("size_chart", "rows", rows);
                          }}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          const rows = settings.size_chart.rows.filter((_, idx) => idx !== i);
                          update("size_chart", "rows", rows);
                        }}
                        className="h-7 w-7 grid place-items-center rounded-full hover:bg-blush-50 text-blush-600"
                      >
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={() => {
              update("size_chart", "rows", [
                ...(settings.size_chart.rows || []),
                { size: "", bust: 0, waist: 0, hip: 0, length: 0 },
              ]);
            }}
            className="mt-4 text-xs uppercase tracking-[0.2em] text-blush-600"
          >
            + Add size row
          </button>
        </Section>
      )}
    </div>
  );
}

function Section({ title, subtitle, children, onSave, onTest, saving }) {
  return (
    <section className="rounded-3xl bg-white border border-blush-100 p-8">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="font-heading text-2xl text-ink-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm font-body text-ink-500">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {onTest && (
            <button onClick={onTest} className="btn-ghost-rose text-xs" data-testid="settings-test-btn">
              Test connection
            </button>
          )}
          <button
            onClick={onSave}
            disabled={saving}
            data-testid="settings-save-btn"
            className="btn-maroon text-xs inline-flex items-center gap-2 disabled:opacity-60"
          >
            <Save size={12} strokeWidth={1.6} /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", step }) {
  return (
    <div>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">{label}</label>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="field mt-2"
      />
    </div>
  );
}

function SecretField({ label, value, onChange, colSpan, hint }) {
  const [show, setShow] = useState(false);
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">{label}</label>
      <div className="relative mt-2">
        <input
          type={show ? "text" : "password"}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="••••••"
          className="field pr-12"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center text-ink-500 hover:text-blush-600"
        >
          {show ? <EyeOff size={14} strokeWidth={1.6} /> : <Eye size={14} strokeWidth={1.6} />}
        </button>
      </div>
      {hint && <p className="mt-1 text-[11px] font-body text-ink-500">{hint}</p>}
    </div>
  );
}

function ModeToggle({ value, onChange }) {
  return (
    <div>
      <label className="text-xs font-body uppercase tracking-[0.2em] text-ink-500">Mode</label>
      <div className="mt-2 inline-flex rounded-full bg-blush-50 p-1 border border-blush-100">
        {["test", "live"].map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onChange(m)}
            data-testid={`razorpay-mode-${m}`}
            className={`px-6 py-2 rounded-full text-xs font-body uppercase tracking-[0.2em] transition ${
              value === m
                ? m === "live" ? "bg-blush-600 text-white" : "bg-white text-blush-600 shadow"
                : "text-ink-700 hover:text-blush-600"
            }`}
          >
            {m}
          </button>
        ))}
      </div>
    </div>
  );
}


